import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  DROPDOWN_ITEM_CLASS,
  DROPDOWN_ITEM_HIGHLIGHT_DATA_CHECKED_CLASS,
  DROPDOWN_ITEM_HIGHLIGHT_DATA_HIGHLIGHTED_CLASS,
  DROPDOWN_ITEM_HIGHLIGHT_HOVER_CLASS,
  DROPDOWN_PANEL_CLASS,
  FORM_CONTROL_DISABLED_CLASS,
  FORM_CONTROL_FOCUS_RING_CLASS,
  FORM_CONTROL_HEIGHT_CLASS,
  FORM_CONTROL_HEIGHT_SM_CLASS,
  FORM_CONTROL_PLACEHOLDER_CLASS,
  FORM_CONTROL_SURFACE_CLASS,
  FORM_CONTROL_TEXT_CLASS,
  FORM_CONTROL_VALUE_CLASS,
} from "@/components/ui/form-control";

/**
 * Re-export tokens so existing `from "@/components/ui/select"` imports keep working.
 */
/* eslint-disable react-refresh/only-export-components -- Backwards-compat re-exports */
export {
  DROPDOWN_ITEM_CLASS,
  DROPDOWN_ITEM_HIGHLIGHT_CLASS,
  DROPDOWN_ITEM_HIGHLIGHT_DATA_CHECKED_CLASS,
  DROPDOWN_ITEM_HIGHLIGHT_DATA_HIGHLIGHTED_CLASS,
  DROPDOWN_ITEM_HIGHLIGHT_DATA_SELECTED_CLASS,
  DROPDOWN_ITEM_HIGHLIGHT_FOCUS_CLASS,
  DROPDOWN_ITEM_HIGHLIGHT_HOVER_CLASS,
  FORM_CONTROL_HEIGHT_CLASS,
  FORM_CONTROL_TEXT_CLASS,
  FORM_CONTROL_VALUE_CLASS,
} from "@/components/ui/form-control";
/* eslint-enable react-refresh/only-export-components */



const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  /**
   * `sm` (36px) for dense surfaces — toolbar filters, controls inside a card
   * header. Use this rather than passing `h-9` in `className`: the height token
   * is applied after the consumer's classes, so a local height is discarded.
   */
  size?: "default" | "sm";
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, size = "default", ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      FORM_CONTROL_SURFACE_CLASS,
      "flex w-full items-center justify-between rounded-md px-3 py-2 transition-colors duration-150 [&>span]:line-clamp-1",
      FORM_CONTROL_PLACEHOLDER_CLASS,
      FORM_CONTROL_DISABLED_CLASS,
      FORM_CONTROL_FOCUS_RING_CLASS,
      "outline-none data-[state=open]:ring-2 data-[state=open]:ring-field-ring data-[state=open]:ring-offset-2",
      className,
      /** Last so local overrides cannot shrink text, height, or value style */
      size === "sm" ? FORM_CONTROL_HEIGHT_SM_CLASS : FORM_CONTROL_HEIGHT_CLASS,
      FORM_CONTROL_TEXT_CLASS,
      FORM_CONTROL_VALUE_CLASS,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 text-field-placeholder" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        DROPDOWN_PANEL_CLASS,
        "relative z-[100] max-h-96 min-w-[8rem] overflow-hidden rounded-md shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      DROPDOWN_ITEM_CLASS,
      "pl-8 pr-2 text-foreground",
      DROPDOWN_ITEM_HIGHLIGHT_DATA_HIGHLIGHTED_CLASS,
      DROPDOWN_ITEM_HIGHLIGHT_DATA_CHECKED_CLASS,
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
